import { fmt2, getSectionLabelShort } from "@/lib/sqaafLabels";

export const POLICY_PAGE_SIZE = 8;

export function truncatePolicyQuestion(text, maxLen = 46) {
  if (!text || typeof text !== "string") return "";
  const t = text.trim();
  return t.length <= maxLen ? t : `${t.slice(0, maxLen).trim()}...`;
}

export function buildPolicyKpis(intervention = {}, overview = {}) {
  const summary = intervention?.intervention_summary || {};
  const blocks = [...(intervention?.block_intervention_index || [])].sort(
    (a, b) => (b.intervention_index ?? 0) - (a.intervention_index ?? 0)
  );
  const topBlock = blocks[0];
  const priorityCount = intervention?.priority_domains?.length ?? 5;

  return {
    criticalCount: Number(summary.critical_count ?? overview?.band_counts?.Critical ?? 0),
    below60Count: Number(summary.below_60_count ?? overview?.below_60_count ?? 0),
    priorityCount,
    topBlock: topBlock?.block || "—",
    topBlockIndex: topBlock ? fmt2(topBlock.intervention_index) : "—",
  };
}

export function filterCriticalSchools(schools = [], { search = "", block = "", band = "" } = {}) {
  let rows = [...schools];
  const q = search.trim().toLowerCase();
  if (q) {
    rows = rows.filter(
      (s) =>
        String(s.school_code || "").toLowerCase().includes(q) ||
        String(s.school_name || "").toLowerCase().includes(q)
    );
  }
  if (block) rows = rows.filter((s) => s.block === block);
  if (band) rows = rows.filter((s) => s.Band === band);
  rows.sort((a, b) => Number(a["SQAAF_Score_%"] ?? 0) - Number(b["SQAAF_Score_%"] ?? 0));
  return rows;
}

export function extractBlockOptions(schools = [], blockIndex = []) {
  const fromSchools = schools.map((s) => s.block).filter(Boolean);
  const fromIndex = blockIndex.map((b) => b.block).filter(Boolean);
  return ["", ...Array.from(new Set([...fromSchools, ...fromIndex])).sort()];
}

export function buildDomainRows(priorityDomains = []) {
  return (priorityDomains || []).slice(0, 5).map((d) => ({
    label: d.section_label ? `Section ${d.section}` : `Section ${d.section}`,
    mean: `${fmt2(d.mean)}%`,
  }));
}

export function buildQuestionChartRows(bottomQuestions = []) {
  return (bottomQuestions || []).slice(0, 10).map((q) => {
    const score = Number(q["mean_score_%"] ?? 0);
    return {
      label: truncatePolicyQuestion(q.question),
      fullLabel: q.question,
      width: Math.max(4, Math.min(100, score)),
      score: fmt2(score),
    };
  });
}

export function buildBlockIndexBars(blockIndex = []) {
  const sorted = [...(blockIndex || [])].sort((a, b) => (b.intervention_index ?? 0) - (a.intervention_index ?? 0));
  const max = sorted[0]?.intervention_index || 1;
  return sorted.map((b) => ({
    block: b.block,
    index: fmt2(b.intervention_index),
    heightPct: Math.max(18, (Number(b.intervention_index) / max) * 100),
    title: `${b.block}: ${fmt2(b.intervention_index)}`,
  }));
}

export function buildIndexTableRows(blockIndex = []) {
  const sorted = [...(blockIndex || [])].sort((a, b) => (b.intervention_index ?? 0) - (a.intervention_index ?? 0));
  return {
    blocks: sorted.map((b) => b.block),
    rows: [
      { label: "Index", values: sorted.map((b) => fmt2(b.intervention_index)) },
      { label: "% Critical", values: sorted.map((b) => `${fmt2(b.critical_pct)}%`) },
      { label: "% < 60", values: sorted.map((b) => `${fmt2(b.pct_below_60)}%`) },
      { label: "Schools", values: sorted.map((b) => String(b.schools ?? 0)) },
    ],
  };
}

export function buildPaginationModel(page, totalItems, pageSize = POLICY_PAGE_SIZE) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const current = Math.min(Math.max(0, page), totalPages - 1);
  const start = totalItems === 0 ? 0 : current * pageSize + 1;
  const end = Math.min(totalItems, (current + 1) * pageSize);

  const pages = [];
  if (totalPages <= 7) {
    for (let i = 0; i < totalPages; i += 1) pages.push(i);
  } else {
    pages.push(0);
    if (current > 2) pages.push("ellipsis-left");
    for (let i = Math.max(1, current - 1); i <= Math.min(totalPages - 2, current + 1); i += 1) {
      if (!pages.includes(i)) pages.push(i);
    }
    if (current < totalPages - 3) pages.push("ellipsis-right");
    pages.push(totalPages - 1);
  }

  return { current, totalPages, start, end, pages };
}

export function getSectionDisplay(section) {
  if (!section) return "—";
  const short = getSectionLabelShort(section);
  return short && short !== section ? `Section ${section}` : `Section ${section}`;
}

export function buildPolicyInfoCard(intervention = {}, overview = {}) {
  const summary = intervention?.intervention_summary || {};
  const topBlock = [...(intervention?.block_intervention_index || [])].sort(
    (a, b) => (b.intervention_index ?? 0) - (a.intervention_index ?? 0)
  )[0];
  const criticalCount = Number(summary.critical_count ?? overview?.band_counts?.Critical ?? 0);
  const below60 = Number(summary.below_60_count ?? overview?.below_60_count ?? 0);
  const priorityDomains = intervention?.priority_domains || [];

  if (topBlock) {
    return {
      title: `Focus on ${topBlock.block} — intervention index ${fmt2(topBlock.intervention_index)}`,
      body: `${criticalCount.toLocaleString()} critical schools and ${below60.toLocaleString()} sub-60% schools district-wide. ${topBlock.block} has ${fmt2(topBlock.critical_pct)}% critical and ${fmt2(topBlock.pct_below_60)}% below 60%. Weakest domain: section ${priorityDomains[0]?.section || "—"} (${fmt2(priorityDomains[0]?.mean)}%).`,
    };
  }
  return {
    title: "Focus policy action where workload is highest",
    body: `${criticalCount.toLocaleString()} critical schools and ${below60.toLocaleString()} sub-60% schools need targeted interventions. Priority domain: section ${priorityDomains[0]?.section || "—"} (${fmt2(priorityDomains[0]?.mean)}%).`,
  };
}
